import {
  BASE_FORMAT_OPTIONS,
  type DateTimePreset,
  PRESET_OPTIONS
} from './presets'

/**
 * Accepted input. A `Date` is treated as an instant (absolute moment), never as
 * a local-calendar date. `number` is intentionally excluded — epoch seconds vs
 * milliseconds is an easy footgun (OCE-1226, D-8); pass an ISO string instead.
 */
export type DateInput = string | Date | null | undefined

export const DEFAULT_FALLBACK = '—'

type FormatOptions = {
  locale: string
  preset: DateTimePreset
  /** undefined → runtime zone (browser locally); 'UTC' → deterministic. */
  timeZone?: string
  fallback?: string
}

type CalendarFormatOptions = Omit<FormatOptions, 'timeZone'>

function toDate(value: DateInput): Date | null {
  if (value == null) {
    return null
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function withTimeZone(
  options: Intl.DateTimeFormatOptions,
  timeZone?: string
): Intl.DateTimeFormatOptions {
  return timeZone ? { ...options, timeZone } : options
}

/** Year of `date` as seen in `timeZone` (or runtime zone) — calendar-aware. */
function yearInZone(date: Date, locale: string, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat(
    locale,
    withTimeZone({ ...BASE_FORMAT_OPTIONS, year: 'numeric' }, timeZone)
  ).formatToParts(date)
  return parts.find(part => part.type === 'year')?.value ?? ''
}

function formatNewsDate(
  date: Date,
  locale: string,
  timeZone: string | undefined
): string {
  const options: Intl.DateTimeFormatOptions = {
    ...BASE_FORMAT_OPTIONS,
    month: 'long',
    day: 'numeric'
  }
  // Compare the date's year and "now" in the SAME zone, otherwise an explicit
  // timeZone makes a getFullYear()-based check lie at the year boundary (D-6).
  if (
    yearInZone(date, locale, timeZone) !==
    yearInZone(new Date(), locale, timeZone)
  ) {
    options.year = 'numeric'
  }
  return new Intl.DateTimeFormat(
    locale,
    withTimeZone(options, timeZone)
  ).format(date)
}

function format(value: DateInput, options: FormatOptions): string {
  const { locale, preset, timeZone, fallback = DEFAULT_FALLBACK } = options
  const date = toDate(value)
  if (!date) {
    return fallback
  }
  if (preset === 'newsDate') {
    return formatNewsDate(date, locale, timeZone)
  }
  return new Intl.DateTimeFormat(
    locale,
    withTimeZone(
      { ...BASE_FORMAT_OPTIONS, ...PRESET_OPTIONS[preset] },
      timeZone
    )
  ).format(date)
}

/**
 * Format an instant (absolute moment) for display. Leave `timeZone` undefined to
 * use the viewer's runtime zone (browser when rendered client-side); pass `'UTC'`
 * for a deterministic pre-hydration fallback. Use for `completed_at`, `created_at`,
 * `joined_at`, etc.
 */
export function formatInstant(
  value: DateInput,
  options: FormatOptions
): string {
  return format(value, options)
}

/**
 * Format a calendar date (a day with no zone/time, e.g. a news publish date or an
 * i18n date string like `2026-03-18`). Always formatted in UTC so the day never
 * shifts across zones (`new Date('2026-03-18')` is UTC midnight → would become the
 * 17th in western zones otherwise). Deterministic → no hydration concern (D-6).
 */
export function formatCalendarDate(
  value: DateInput,
  options: CalendarFormatOptions
): string {
  return format(value, { ...options, timeZone: 'UTC' })
}

/**
 * Month index (0-11) and year of an instant as seen in `timeZone` (or the runtime
 * zone when omitted). For callers that localize the month name themselves (e.g.
 * via i18n message keys) instead of through Intl. Returns null for invalid input.
 */
export function getDatePartsInZone(
  value: DateInput,
  timeZone?: string
): { monthIndex: number; year: number } | null {
  const date = toDate(value)
  if (!date) {
    return null
  }
  const parts = new Intl.DateTimeFormat(
    'en-US',
    withTimeZone(
      { ...BASE_FORMAT_OPTIONS, month: 'numeric', year: 'numeric' },
      timeZone
    )
  ).formatToParts(date)
  const month = parts.find(part => part.type === 'month')?.value
  const year = parts.find(part => part.type === 'year')?.value
  if (!month || !year) {
    return null
  }
  return { monthIndex: Number(month) - 1, year: Number(year) }
}
